import React, { useMemo, useState } from 'react';
import { Card, Stack, Typography, Avatar, Box, Grid, Button, Popover } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import DataNotFound from '../../../Components/DataNotFound';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function StatisticsCirclePointsCard({
	title = 'Category Breakdown',
	values = [],
	labels = [],
	colors = [],
	total,
	height = 300,
	size = 175,
}) {
	// Helper: get color for index with diminishing brightness when reusing the palette of 4 colors
	const getColorForIndex = useMemo(() => {
		// convert hex to rgb
		const hexToRgb = (hex) => {
			const clean = hex?.replace('#', '');
			if (!clean || (clean.length !== 3 && clean.length !== 6)) return null;
			const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
			const r = parseInt(full.substring(0, 2), 16);
			const g = parseInt(full.substring(2, 4), 16);
			const b = parseInt(full.substring(4, 6), 16);
			return { r, g, b };
		};

		// apply brightness factor (0..1) to rgb
		const applyBrightness = (hex, factor) => {
			const rgb = hexToRgb(hex);
			if (!rgb) return hex;
			const r = Math.round(rgb.r * factor);
			const g = Math.round(rgb.g * factor);
			const b = Math.round(rgb.b * factor);
			return `rgb(${r}, ${g}, ${b})`;
		};

		return (idx) => {
			if (!colors || colors.length === 0) return '#999999';
			const baseIdx = idx % colors.length; // reuse within 0..3
			const reuseCount = Math.floor(idx / colors.length); // 0 for first cycle, 1 for second, etc.
			const baseColor = colors[baseIdx] || '#999999';
			// brightness factor: first cycle 1.0, then 0.85, 0.7, 0.55...
			const factor = Math.max(0.35, 1 - reuseCount * 0.15);
			return applyBrightness(baseColor, factor);
		};
	}, [colors]);

	const [anchorEl, setAnchorEl] = useState(null);
	const hasMore = labels?.length > 3;
	const visibleLabels = labels?.slice(0, 3) || [];
	const moreLabels = labels?.slice(3) || [];

	const data = {
		labels: labels,
		datasets: [
			{
				data: values,
				backgroundColor: (values || []).map((_, idx) => getColorForIndex(idx)),
				borderWidth: 0,
				cutout: '80%', // Creates a ring effect
				hoverBorderWidth: 1,
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				enabled: true,
				callbacks: {
					label: (context) => `${context.label}: ${context.parsed}`,
				},
			},
		},
		elements: {
			arc: {
				// borderRadius: 4, // Rounded segments
			},
		},
	};

	return (
		<Card
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'space-between',
				borderRadius: '12px',
				height: height,
				width: '100%',
				height: '100%',
				padding: '16px',
				boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
			}}
		>
			{values.length === 0 ? (
				<DataNotFound text="No data available to display the chart." size={100} />
			) : (
				<>
					{/* Title at Top Start */}
					<Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%" mb={2}>
						<Typography
							variant="h6"
							fontFamily="Poppins"
							fontWeight={600}
							sx={{
								fontSize: '14px',
							}}
						>
							{title}
						</Typography>
					</Stack>

					{/* Chart Container in Center */}
					<Box
						sx={{
							position: 'relative',
							width: size,
							height: size,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexGrow: 1,
						}}
					>
						{/* Doughnut Chart */}
						<Doughnut data={data} options={options} />

						{/* Center Total */}
						<Box
							sx={{
								position: 'absolute',
								top: '50%',
								left: '50%',
								transform: 'translate(-50%, -50%)',
								zIndex: 1,
								textAlign: 'center',
							}}
						>
							<Typography
								variant="h4"
								fontWeight={700}
								sx={{
									color: '#333',
									fontSize: '24px',
								}}
							>
								{total}
							</Typography>
							<Typography
								variant="body2"
								fontWeight={500}
								sx={{
									color: '#666',
									fontSize: '12px',
								}}
							>
								Reasons
							</Typography>
						</Box>
					</Box>

					{/* Bullet Points */}
					<Box sx={{ width: '100%', position: 'relative' }}>
						<Grid container rowSpacing={1} columnSpacing={2} sx={{ mt: 2 }}>
							{visibleLabels.map((label, idx) => (
								<Grid item xs={6} key={idx}>
									<Stack direction="row" alignItems="center" spacing={1}>
										<Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: getColorForIndex(idx) }} />
										<Typography variant="body2" fontSize={12} fontWeight={500} sx={{ textTransform: 'capitalize' }}>
											{label}
										</Typography>
									</Stack>
								</Grid>
							))}
						</Grid>

						{hasMore && (
							<Stack sx={{ position: 'absolute', right: 0, bottom: -6 }}>
								<Button
									variant="text"
									size="small"
									onClick={(e) => setAnchorEl(e.currentTarget)}
									sx={{ textTransform: 'none', fontSize: 12 }}
								>
									Show more
								</Button>
							</Stack>
						)}

						<Popover
							open={Boolean(anchorEl)}
							anchorEl={anchorEl}
							onClose={() => setAnchorEl(null)}
							anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
							transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						>
							<Box sx={{ p: 1.5, maxWidth: 260 }}>
								<Grid container rowSpacing={1} columnSpacing={2}>
									{moreLabels.map((label, i) => {
										const idx = i + visibleLabels.length;
										return (
											<Grid item xs={12} key={`${label}-${idx}`}>
												<Stack direction="row" alignItems="center" spacing={1}>
													<Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: getColorForIndex(idx) }} />
													<Typography variant="body2" fontSize={12} fontWeight={500} sx={{ textTransform: 'capitalize' }}>
														{label}
													</Typography>
												</Stack>
											</Grid>
										);
									})}
								</Grid>
							</Box>
						</Popover>
					</Box>
				</>
			)}
		</Card>
	);
}
